import { type AxiosError } from "axios";
import { UploadExcelMessages, type UploadExcelErrors } from "~/api/operational";
import { excelUtils, type ExcelError } from "@/utils/excel";

export const useExcel = ({
  request,
  onSuccess,
  onError,
}: {
  request: (file: File) => Promise<any>;
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  const isLoading = ref(false);
  const isSuccess = ref(false);
  const error = ref<ExcelError | undefined>();

  const excelFile = ref<File>();

  /**
   * Envia a planilha e trata os erros
   */
  async function handleSubmit(file?: File) {
    const _file = file || excelFile.value;
    if (!_file) {
      useNuxtApp().$toast("Nenhum arquivo selecionado!", {
        type: "error",
      });
      return;
    }
    isLoading.value = true;
    try {
      await request(_file);

      onSuccess?.();

      isSuccess.value = true;
    } catch (requestError) {
      isLoading.value = false;
      const _error = requestError as AxiosError<UploadExcelErrors>;
      try {
        makeExcelErrors(_error);
      } catch (parseError) {
        // O backend retornou um payload de erro em formato inesperado
        // (ex: JSON inválido em header_errors) e o parse quebrou.
        error.value = { type: "generic" };
        useNuxtApp().$error({
          error: parseError,
          message: "Erro ao processar retorno da planilha!",
          extra: { response_data: _error.response?.data },
        });
      }
      if (_error.response?.status && _error.response.status >= 500) {
        useNuxtApp().$error({
          error: requestError,
          message: "Erro ao enviar planilha!",
        });
      }
      onError?.();
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Transforma o erro da requisição
   */
  function makeExcelErrors(_error: AxiosError<UploadExcelErrors>) {
    // Erro genérico
    if (_error.response?.status && _error.response?.status >= 500) {
      error.value = {
        type: "generic",
      };
      return;
    }

    const { data } = _error.response || {};
    if (data) {
      switch (data.message) {
        // Planilha vazia
        case UploadExcelMessages.EMPTY_SHEET:
          error.value = {
            type: "empty",
          };
          return;
        // Cabeçalho fora da primeira linha
        case UploadExcelMessages.HEADER_NOT_FIRST_LINE:
          error.value = {
            type: "header_not_first_line",
          };
          return;
        // Colunas com erro
        case UploadExcelMessages.DIVERGENT_COLS:
          error.value = {
            type: "divergent_columns",
            divergentColumns: excelUtils.parseDivergentCols(data.header_errors),
          };
          return;
        // Células com erro
        case UploadExcelMessages.CELLS_WITH_ERROR:
          error.value = {
            type: "cells_with_error",
            cellsWithError: excelUtils.parseCellsWithError(data.data_errors),
          };
          return;
      }
    }
  }

  function reset() {
    excelFile.value = undefined;
    isSuccess.value = false;
    error.value = undefined;
  }

  return {
    handleSubmit,
    error,
    isSuccess,
    isLoading,
    excelFile,
    reset,
  };
};
